import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Transaction, BudgetCategory, ScreenType, FinancialHealth, WealthItem, WealthMetrics, RecurringFrequency } from './types';
import { HUD } from './components/HUD';
import { Dashboard } from './components/screens/Dashboard';
import { BudgetPlanner } from './components/screens/BudgetPlanner';
import { TransactionLedger } from './components/screens/TransactionLedger';
import { FinancialAnalytics } from './components/screens/FinancialAnalytics';
import { WealthAnalyzer } from './components/screens/WealthAnalyzer';
import { EmergencyFundScreen } from './components/screens/EmergencyFund';
import { InvestmentSimScreen } from './components/screens/InvestmentSim';
import { PortfolioAnalyzerScreen } from './components/screens/PortfolioAnalyzer';
import { LegacyPlannerScreen } from './components/screens/LegacyPlanner';
import { UserManual } from './components/screens/UserManual';
import { HelpModal } from './components/HelpModal';
import { LandingPage } from './components/LandingPage';
import { INITIAL_CATEGORIES } from './constants';
import { LayoutGrid, ClipboardList, PenTool, PieChart, ShieldCheck, Calendar as CalendarIcon, ChevronLeft, ChevronRight, RotateCcw, X } from 'lucide-react';

export default function App() {
  const [hasStarted, setHasStarted] = useState<boolean>(() => {
    return localStorage.getItem('stackup_started') === 'true';
  });

  const [transactions, setTransactions] = useState<Transaction[]>(() => {
    const saved = localStorage.getItem('stackup_txs');
    return saved ? JSON.parse(saved) : [];
  });

  const [categories, setCategories] = useState<BudgetCategory[]>(() => {
    const saved = localStorage.getItem('stackup_categories');
    return saved ? JSON.parse(saved) : INITIAL_CATEGORIES;
  });

  const [wealthItems, setWealthItems] = useState<WealthItem[]>(() => {
    const saved = localStorage.getItem('stackup_wealth');
    return saved ? JSON.parse(saved) : [];
  });

  const [importStatus, setImportStatus] = useState<{message: string, type: 'error' | 'success' | 'info'} | null>(null);

  const todayMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const [selectedMonth, setSelectedMonth] = useState<string>(todayMonth);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const [currentScreen, setCurrentScreen] = useState<ScreenType>('dashboard');
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('stackup_txs', JSON.stringify(transactions));
    localStorage.setItem('stackup_categories', JSON.stringify(categories));
    localStorage.setItem('stackup_wealth', JSON.stringify(wealthItems));
    localStorage.setItem('stackup_started', hasStarted.toString());
  }, [transactions, categories, wealthItems, hasStarted]);

  // AUTO-GENERATION LOGIC FOR RECURRING TRANSACTIONS
  useEffect(() => {
    const masters = transactions.filter(t => t.isRecurring && !t.parentId);
    const existingInstances = transactions.filter(t => t.parentId);
    const newInstances: Transaction[] = [];

    masters.forEach(master => {
      // Check if an instance of this master exists for the currently selected month
      const hasInstance = existingInstances.some(i => i.parentId === master.id && i.date.startsWith(selectedMonth));
      
      // If no instance exists and the master started on or before this month, spawn it
      const masterMonth = master.date.slice(0, 7);
      if (!hasInstance && masterMonth <= selectedMonth) {
        // Derive date for this month: Use same day as master
        const [mYear, mMonth, mDay] = master.date.split('-');
        const [sYear, sMonth] = selectedMonth.split('-');
        
        // Handle end-of-month day overflows (e.g. Feb 31st)
        const d = new Date(parseInt(sYear), parseInt(sMonth), 0);
        const lastDay = d.getDate();
        const finalDay = Math.min(parseInt(mDay), lastDay);
        const newDate = `${sYear}-${sMonth}-${String(finalDay).padStart(2, '0')}`;

        newInstances.push({
          ...master,
          id: `instance-${master.id}-${selectedMonth}`,
          date: newDate,
          parentId: master.id,
          isRecurring: false // Instance itself isn't a template
        });
      }
    });

    if (newInstances.length > 0) {
      setTransactions(prev => [...prev, ...newInstances]);
    }
  }, [selectedMonth, transactions.length]); // Re-run when month changes or masters are added

  const metrics = useMemo<FinancialHealth>(() => {
    const filteredTxs = transactions.filter(t => t.date.startsWith(selectedMonth));
    const income = filteredTxs.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expenses = filteredTxs.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const surplus = income - expenses;
    const savingsRate = income > 0 ? (surplus / income) * 100 : 0;
    
    const updatedCategories = categories.map(cat => {
      const actual = filteredTxs
        .filter(t => t.type === 'expense' && t.category === cat.label)
        .reduce((sum, t) => sum + t.amount, 0);
      return { ...cat, actual };
    });

    return { 
      income, expenses, surplus, 
      savingsRate: Math.round(savingsRate), 
      dailyGuidance: 0, 
      score: income > 0 ? Math.min(100, Math.max(0, 50 + (Math.round(savingsRate) / 2))) : 0, 
      categories: updatedCategories 
    };
  }, [transactions, categories, selectedMonth]);

  const wealthMetrics = useMemo<WealthMetrics>(() => {
    const totalAssets = wealthItems.filter(i => i.type === 'asset').reduce((sum, i) => sum + i.amount, 0);
    const totalLiabilities = wealthItems.filter(i => i.type === 'liability').reduce((sum, i) => sum + i.amount, 0);
    const liquid = wealthItems.filter(i => i.subType === 'liquid').reduce((sum, i) => sum + i.amount, 0);
    const nav = totalAssets - totalLiabilities;
    const debtRatio = totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;
    const annualExpenses = (metrics.expenses || 5000) * 12;
    const targetFreedom = annualExpenses * 25;
    return { totalAssets, totalLiabilities, nav, debtRatio, liquidity: liquid, freedomProgress: targetFreedom > 0 ? (nav / targetFreedom) * 100 : 0 };
  }, [wealthItems, metrics.expenses]);

  const addTransaction = (name: string, amount: number, type: 'income' | 'expense', category: string, date?: string, isRecurring?: boolean, frequency?: RecurringFrequency) => {
    const newTx: Transaction = { 
      id: Date.now().toString(), 
      name, 
      amount, 
      type, 
      category, 
      date: date || new Date().toISOString().split('T')[0],
      isRecurring,
      frequency
    };
    setTransactions(prev => [newTx, ...prev]);
  };

  const bulkAddTransactions = (newTxs: Transaction[]) => {
    setTransactions(prev => [...newTxs, ...prev]);
    setImportStatus({ message: `Successfully committed ${newTxs.length} transactions to ledger.`, type: 'success' });
  };

  const deleteTransaction = (id: string) => {
    setTransactions(prev => prev.filter(t => t.id !== id && t.parentId !== id));
  };

  const bulkDeleteTransactions = (ids: string[]) => {
    setTransactions(prev => prev.filter(t => !ids.includes(t.id) && !(t.parentId && ids.includes(t.parentId))));
    setImportStatus({ message: `Successfully deleted ${ids.length} entries and their associated instances.`, type: 'info' });
  };

  const bulkUpdateRecurringStatus = (ids: string[], isRecurring: boolean, frequency: RecurringFrequency = 'monthly') => {
    setTransactions(prev => prev.map(t => {
      if (ids.includes(t.id)) {
        return { ...t, isRecurring, frequency: isRecurring ? frequency : undefined };
      }
      return t;
    }));
    setImportStatus({ message: `Bulk updated recurring rules for ${ids.length} transactions.`, type: 'success' });
  };

  const processCSV = (text: string): Transaction[] => {
    try {
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      if (lines.length < 2) {
        setImportStatus({ message: "Import failed: File is empty or has no header row.", type: 'error' });
        return [];
      }

      const splitLine = (line: string) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let char of line) {
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      const headers = splitLine(lines[0]).map(h => h.toLowerCase().replace(/["']/g, '').trim());
      
      const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('time') || h.includes('period'));
      const descIdx = headers.findIndex(h => h.includes('desc') || h.includes('payee') || h.includes('memo') || h.includes('narrative') || h.includes('transaction') || h.includes('info'));
      const amountIdx = headers.findIndex(h => (h.includes('amount') || h.includes('value') || h.includes('sum') || h.includes('total')) && !h.includes('balance'));
      const moneyInIdx = headers.findIndex(h => h.includes('in') || h.includes('credit') || h.includes('deposit') || h.includes('received'));
      const moneyOutIdx = headers.findIndex(h => h.includes('out') || h.includes('debit') || h.includes('payment') || h.includes('spent') || h.includes('withdrawal'));

      if (dateIdx === -1 || descIdx === -1 || (amountIdx === -1 && moneyInIdx === -1 && moneyOutIdx === -1)) {
        setImportStatus({ 
          message: "Structure mismatch: Required columns (Date, Description, Amount) not found. Check your CSV headers.", 
          type: 'error' 
        });
        return [];
      }

      const tryParseDate = (dateStr: string): string => {
        if (!dateStr) return '';
        const clean = dateStr.trim();
        const d = new Date(clean);
        if (!isNaN(d.getTime())) {
          if (clean.length > 4) return d.toISOString().split('T')[0];
        }
        const parts = clean.split(/[/\-.]/);
        if (parts.length === 3) {
          let day, month, year;
          if (parts[0].length === 4) {
            year = parts[0]; month = parts[1]; day = parts[2];
          } else if (parts[2].length === 4 || parts[2].length === 2) {
            year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
            const p1 = parseInt(parts[0]);
            const p2 = parseInt(parts[1]);
            if (p1 > 12) { day = parts[0]; month = parts[1]; }
            else if (p2 > 12) { day = parts[1]; month = parts[0]; }
            else { day = parts[0]; month = parts[1]; }
          }
          if (year && month && day) {
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }
        return '';
      };

      const tryParseAmount = (amtStr: string): number => {
        if (!amtStr) return NaN;
        let s = amtStr.replace(/[^\d.,\-()]/g, '').trim();
        if (s.startsWith('(') && s.endsWith(')')) s = '-' + s.slice(1, -1);
        const lastComma = s.lastIndexOf(',');
        const lastDot = s.lastIndexOf('.');
        if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
        else s = s.replace(/,/g, '');
        return parseFloat(s);
      };

      const newTransactions: Transaction[] = [];
      let skippedRows = 0;

      for (let i = 1; i < lines.length; i++) {
        const parts = splitLine(lines[i]);
        if (parts.length < 2) continue;

        const dateStr = parts[dateIdx];
        const date = tryParseDate(dateStr);
        const description = parts[descIdx]?.replace(/["']/g, '') || 'Imported Entry';
        
        let finalAmount = 0;
        let finalType: 'income' | 'expense' = 'expense';

        if (moneyInIdx !== -1 || moneyOutIdx !== -1) {
          const inVal = moneyInIdx !== -1 ? tryParseAmount(parts[moneyInIdx]) : NaN;
          const outVal = moneyOutIdx !== -1 ? tryParseAmount(parts[moneyOutIdx]) : NaN;
          
          if (!isNaN(inVal) && inVal !== 0) {
            finalAmount = Math.abs(inVal);
            finalType = 'income';
          } else if (!isNaN(outVal) && outVal !== 0) {
            finalAmount = Math.abs(outVal);
            finalType = 'expense';
          } else {
            skippedRows++;
            continue;
          }
        } else {
          const rawAmount = tryParseAmount(parts[amountIdx]);
          if (isNaN(rawAmount) || !date) {
            skippedRows++;
            continue;
          }
          finalAmount = Math.abs(rawAmount);
          finalType = rawAmount < 0 ? 'expense' : 'income';
        }

        if (!date) {
          skippedRows++;
          continue;
        }

        newTransactions.push({
          id: `imported-${Date.now()}-${i}`,
          name: description,
          amount: finalAmount,
          type: finalType,
          category: 'Imported',
          date: date
        });
      }

      if (newTransactions.length === 0) {
        setImportStatus({ message: "Import failed: Zero valid transactions extracted. Check date and amount formats.", type: 'error' });
        return [];
      }

      const statusMsg = skippedRows > 0 
        ? `Partial Import: ${newTransactions.length} records ready. ${skippedRows} rows skipped due to malformed dates/amounts.`
        : `Ready: ${newTransactions.length} transactions successfully mapped and normalized.`;
      
      setImportStatus({ message: statusMsg, type: skippedRows > 0 ? 'info' : 'success' });
      return newTransactions;
    } catch (err) {
      setImportStatus({ message: "System Error: The CSV parser encountered an unrecoverable row structure.", type: 'error' });
      return [];
    }
  };

  const addCategory = (label: string, limit: number, color: string) => {
    const newCat: BudgetCategory = { label, limit, actual: 0, color };
    setCategories(prev => [...prev, newCat]);
  };

  const updateCategory = (index: number, update: Partial<BudgetCategory>) => {
    setCategories(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...update };
      return updated;
    });
  };

  const deleteCategory = (label: string) => {
    setCategories(prev => prev.filter(c => c.label !== label));
  };

  const resetToStandardCategories = () => {
    setCategories(INITIAL_CATEGORIES);
  };

  const addWealthItem = (name: string, amount: number, type: 'asset' | 'liability', subType: string) => {
    const newItem: WealthItem = {
      id: Date.now().toString(),
      name,
      amount,
      type,
      subType: subType as any
    };
    setWealthItems(prev => [...prev, newItem]);
  };

  const deleteWealthItem = (id: string) => {
    setWealthItems(prev => prev.filter(i => i.id !== id));
  };

  const changeMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 15);
    if (direction === 'prev') date.setMonth(date.getMonth() - 1);
    else date.setMonth(date.getMonth() + 1);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${y}-${m}`);
  };

  const formattedMonth = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 15).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  }, [selectedMonth]);

  if (!hasStarted) return <LandingPage onStart={() => setHasStarted(true)} />;

  return (
    <div className="min-h-screen bg-finBackground font-sans text-navy pb-32 overflow-x-hidden selection:bg-emerald/10">
      <HUD metrics={metrics} wealthMetrics={wealthMetrics} onOpenHelp={() => setIsHelpOpen(true)} />
      
      {/* Global Month Selection Bar */}
      <div className="max-w-4xl mx-auto px-6 mt-6 flex justify-between items-center bg-white border border-slate-100 py-3 px-6 rounded-2xl shadow-sm relative z-[80]">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsPickerOpen(!isPickerOpen)}
            className={`p-2 rounded-lg transition-colors ${isPickerOpen ? 'bg-electric text-white' : 'bg-slate-50 text-slate-400 hover:text-electric'}`}
          >
            <CalendarIcon size={16} />
          </button>
          <button 
            onClick={() => setIsPickerOpen(!isPickerOpen)}
            className="text-xs font-black text-navy uppercase tracking-widest hover:text-electric transition-colors"
          >
            {formattedMonth}
          </button>
          
          {selectedMonth !== todayMonth && (
            <button 
              onClick={() => { setSelectedMonth(todayMonth); setIsPickerOpen(false); }}
              className="ml-2 flex items-center gap-1.5 px-3 py-1 bg-electric/10 text-electric text-[9px] font-black uppercase tracking-widest rounded-full hover:bg-electric hover:text-white transition-all animate-fade-in"
            >
              <RotateCcw size={10} /> Current
            </button>
          )}

          {/* Temporal Picker Overlay */}
          {isPickerOpen && (
            <div className="absolute top-14 left-0 w-72 bg-navy rounded-3xl shadow-2xl p-6 text-white border border-white/10 animate-fade-in z-[100]">
              <div className="flex justify-between items-center mb-6">
                <span className="text-[10px] font-black uppercase tracking-widest text-sky">Temporal Console</span>
                <button onClick={() => setIsPickerOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
              </div>
              <div className="flex justify-between items-center mb-6 px-1">
                <button onClick={() => changeMonth('prev')} className="p-1 hover:bg-white/10 rounded-lg"><ChevronLeft size={16} /></button>
                <span className="font-bold text-sm">{selectedMonth.split('-')[0]}</span>
                <button onClick={() => changeMonth('next')} className="p-1 hover:bg-white/10 rounded-lg"><ChevronRight size={16} /></button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, idx) => {
                  const monthStr = String(idx + 1).padStart(2, '0');
                  const isSelected = selectedMonth.split('-')[1] === monthStr;
                  return (
                    <button
                      key={m}
                      onClick={() => {
                        const year = selectedMonth.split('-')[0];
                        setSelectedMonth(`${year}-${monthStr}`);
                        setIsPickerOpen(false);
                      }}
                      className={`py-2 rounded-xl text-[10px] font-black uppercase transition-all ${isSelected ? 'bg-electric text-white' : 'hover:bg-white/5 text-slate-400'}`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => changeMonth('prev')} className="p-2 hover:bg-slate-50 rounded-lg transition-colors text-slate-400 hover:text-navy">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => changeMonth('next')} className="p-2 hover:bg-slate-50 rounded-lg transition-colors text-slate-400 hover:text-navy">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {currentScreen === 'dashboard' && <Dashboard metrics={metrics} wealthMetrics={wealthMetrics} onNavigate={(s: ScreenType) => setCurrentScreen(s)} />}
        {currentScreen === 'ledger' && (
          <TransactionLedger 
            transactions={transactions} 
            categories={categories} 
            onAdd={addTransaction} 
            onBulkAdd={bulkAddTransactions} 
            onDelete={deleteTransaction} 
            onBulkDelete={bulkDeleteTransactions}
            onBulkUpdateRecurring={bulkUpdateRecurringStatus}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            processCSV={processCSV}
            importStatus={importStatus}
            onClearStatus={() => setImportStatus(null)}
          />
        )}
        {currentScreen === 'planner' && <BudgetPlanner categories={metrics.categories} onAddCategory={addCategory} onUpdateCategory={updateCategory} onDeleteCategory={deleteCategory} onResetToStandard={resetToStandardCategories} />}
        {currentScreen === 'analytics' && <FinancialAnalytics metrics={metrics} transactions={transactions} selectedMonth={formattedMonth} />}
        {currentScreen === 'wealth' && <WealthAnalyzer items={wealthItems} metrics={wealthMetrics} onAdd={addWealthItem} onDelete={deleteWealthItem} />}
        
        {currentScreen === 'emergency' && <EmergencyFundScreen onBack={() => setCurrentScreen('dashboard')} />}
        {currentScreen === 'investment' && <InvestmentSimScreen onBack={() => setCurrentScreen('dashboard')} />}
        {currentScreen === 'portfolio' && <PortfolioAnalyzerScreen onBack={() => setCurrentScreen('dashboard')} />}
        {currentScreen === 'legacy' && <LegacyPlannerScreen onBack={() => setCurrentScreen('dashboard')} />}
        {currentScreen === 'manual' && <UserManual onBack={() => setCurrentScreen('dashboard')} />}
      </main>

      <HelpModal 
        isOpen={isHelpOpen} 
        onClose={() => setIsHelpOpen(false)} 
        onOpenManual={() => {
          setCurrentScreen('manual');
          setIsHelpOpen(false);
        }}
      />

      <nav className="fixed bottom-[env(safe-area-inset-bottom,1.5rem)] left-1/2 -translate-x-1/2 w-[92%] max-w-lg bg-navy/95 backdrop-blur-xl rounded-4xl px-6 py-4 flex justify-between items-center z-[100] shadow-2xl border border-white/5 md:bottom-8" role="navigation" aria-label="Main Navigation">
        <NavButton active={currentScreen === 'dashboard'} onClick={() => setCurrentScreen('dashboard')} Icon={LayoutGrid} label="Home" />
        <NavButton active={currentScreen === 'ledger'} onClick={() => setCurrentScreen('ledger')} Icon={ClipboardList} label="Ledger" />
        <NavButton active={currentScreen === 'wealth'} onClick={() => setCurrentScreen('wealth')} Icon={ShieldCheck} label="Wealth" />
        <NavButton active={currentScreen === 'planner'} onClick={() => setCurrentScreen('planner')} Icon={PenTool} label="Planner" />
        <NavButton active={currentScreen === 'analytics'} onClick={() => setCurrentScreen('analytics')} Icon={PieChart} label="Report" />
      </nav>
    </div>
  );
}

const NavButton = ({ active, onClick, Icon, label }: any) => (
  <button 
    onClick={onClick} 
    className={`flex flex-col items-center gap-1.5 transition-all duration-300 rounded-lg p-1 ${active ? 'text-emerald' : 'text-slate-400 hover:text-white'}`}
  >
    <Icon size={22} strokeWidth={active ? 2.5 : 2} />
    <span className={`text-[9px] font-inter font-bold uppercase tracking-widest ${active ? 'opacity-100' : 'opacity-60'}`}>{label}</span>
  </button>
);